import React, { useState } from 'react';
import '../styles/FeedbackForm.css';
import { trackEvent } from '../utils/analytics';
import { analyticsConfig } from '../config/analytics.config';

interface FeedbackFormProps {
  onClose?: () => void;
}

/**
 * @component FeedbackForm
 * @description A simple feedback form that collects user feedback and sends it via Formspree
 * @example
 * <FeedbackForm onClose={() => setShowFeedback(false)} />
 */
const FeedbackForm: React.FC<FeedbackFormProps> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    rating: '',
    feedback: '',
    email: '',
    page: window.location.pathname,
    timestamp: new Date().toISOString()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Use the configured Formspree endpoint
  const FORM_ENDPOINT = analyticsConfig.formspree.endpoint;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Track the feedback submission event using Umami
      trackEvent('feedback_submitted', { rating: formData.rating });

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setIsSubmitted(true);
      // Reset form after successful submission
      setFormData({
        rating: '',
        feedback: '',
        email: '',
        page: window.location.pathname,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-container">
      {isSubmitted ? (
        <div className="feedback-success">
          <h3>Thank you for your feedback!</h3>
          <p>Your input helps us improve TimeTrakrLite.</p>
          <button 
            className="primary-button" 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      ) : (
        <form className="feedback-form" onSubmit={handleSubmit}>
          <h3>We'd love your feedback!</h3>
          <p>Help us improve TimeTrakrLite by sharing your thoughts.</p>
          
          <div className="form-group">
            <label htmlFor="rating">How would you rate your experience?</label>
            <select 
              id="rating" 
              name="rating" 
              value={formData.rating}
              onChange={handleChange}
              required
            >
              <option value="">Select a rating</option>
              <option value="5">5 - Excellent</option>
              <option value="4">4 - Good</option>
              <option value="3">3 - Average</option>
              <option value="2">2 - Below Average</option>
              <option value="1">1 - Poor</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="feedback">What could we improve?</label>
            <textarea 
              id="feedback"
              name="feedback" 
              value={formData.feedback}
              onChange={handleChange}
              rows={4} 
              required
              placeholder="Your feedback helps us make TimeTrakrLite better..."
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email (optional)</label>
            <input 
              type="email" 
              id="email"
              name="email" 
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
            />
            <small>We'll only use this to follow up on your feedback if needed.</small>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-buttons">
            <button 
              type="button" 
              className="secondary-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="primary-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FeedbackForm;
